/**
 * Single HTML renderer for report print and PDF export.
 */

import {
  buildReportLetterheadHTML,
  REPORT_LETTERHEAD_CSS,
  escapeReportHtml
} from './reportDocumentHeader';
import { REPORT_ARABIC_HTML_FONT_CSS } from './arabicPdfFont';
import { normalizeReportPayload } from './reportDataNormalizer';
import { buildPrintAdsCssVariables, buildPrintAdsInkForceCss } from './printAdsPalette';

export const REPORT_DOCUMENT_CSS = `
  @page {
    size: A4;
    margin: 12mm;
  }
  *, *::before, *::after {
    box-sizing: border-box;
  }
  html, body {
    margin: 0;
    padding: 0;
    background: var(--print-surface, #ffffff);
    color: var(--print-text, #111827);
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body {
    font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
    font-size: 11px;
    line-height: 1.45;
    padding: 16px;
  }
  body[dir="rtl"] {
    font-family: 'Cairo', 'Noto Naskh Arabic', 'Segoe UI', Tahoma, Arial, sans-serif;
  }
  .report-document {
    width: 100%;
    max-width: 100%;
  }
  ${REPORT_LETTERHEAD_CSS}
  .report-summary {
    margin: 20px 0 16px;
    padding: 14px 16px;
    background: var(--print-background);
    border: 1px solid var(--print-border-subtle);
    border-radius: 6px;
    page-break-inside: avoid;
  }
  .report-summary__title {
    margin: 0 0 10px;
    font-size: 13px;
    font-weight: 700;
    color: var(--print-text-secondary);
  }
  .report-summary__table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  .report-summary__table th,
  .report-summary__table td {
    border: 1px solid var(--print-border-subtle);
    padding: 7px 10px;
    vertical-align: top;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .report-summary__table th {
    background: var(--print-primary);
    color: var(--print-text-inverse);
    font-weight: 600;
    width: 55%;
  }
  .report-data-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    margin: 16px 0 0;
    page-break-inside: auto;
  }
  .report-data-table thead {
    display: table-header-group;
  }
  .report-data-table tr {
    page-break-inside: avoid;
  }
  .report-data-table th,
  .report-data-table td {
    border: 1px solid var(--print-secondary-300);
    padding: 8px 7px;
    vertical-align: top;
    text-align: start;
    word-break: break-word;
    overflow-wrap: anywhere;
    hyphens: auto;
    max-width: 0;
    font-size: 10.5px;
    line-height: 1.4;
  }
  .report-data-table th {
    background: var(--print-primary);
    color: var(--print-text-inverse);
    font-weight: 700;
    font-size: 11px;
  }
  .report-data-table tbody tr:nth-child(even) td {
    background: var(--print-secondary-100);
  }
  .report-ltr {
    direction: ltr;
    unicode-bidi: isolate;
    display: inline-block;
    width: 100%;
  }
  .report-cell-datetime {
    font-variant-numeric: tabular-nums;
    white-space: pre-line;
    line-height: 1.35;
  }
  body[dir="rtl"] .report-data-table .report-cell-datetime {
    text-align: right;
  }
  .report-cell-amount {
    direction: ltr;
    unicode-bidi: isolate;
    text-align: end;
    font-variant-numeric: tabular-nums;
  }
  body[dir="rtl"] .report-data-table .report-cell-amount {
    text-align: left;
  }
  .report-empty {
    padding: 24px;
    text-align: center;
    color: var(--print-text-muted);
    border: 1px dashed var(--print-border);
    border-radius: 6px;
    margin-top: 16px;
  }
  .report-footer {
    margin-top: 20px;
    padding-top: 8px;
    border-top: 1px solid var(--print-border-subtle);
    font-size: 9px;
    color: var(--print-text-muted);
    text-align: center;
  }
  @media print {
    body { padding: 0; }
    .report-data-table th,
    .report-summary__table th {
      background: var(--print-primary) !important;
      color: var(--print-text-inverse) !important;
    }
  }
`;

/**
 * Full report stylesheet including resolved ADS print variables.
 */
export function getReportDocumentCss() {
  return `${buildPrintAdsCssVariables()}\n${buildPrintAdsInkForceCss()}\n${REPORT_DOCUMENT_CSS}`;
}

function buildSummaryHtml(report) {
  if (!report.summary?.length) return '';

  const labelHeader = report.isRTL ? 'البند' : 'Élément';
  const valueHeader = report.isRTL ? 'القيمة' : 'Valeur';

  const rows = report.summary
    .map(
      ({ label, value }) =>
        `<tr><td>${escapeReportHtml(label)}</td><td>${escapeReportHtml(value)}</td></tr>`
    )
    .join('');

  return `
    <section class="report-summary">
      <h2 class="report-summary__title">${escapeReportHtml(report.summaryTitle)}</h2>
      <table class="report-summary__table">
        <thead>
          <tr>
            <th>${escapeReportHtml(labelHeader)}</th>
            <th>${escapeReportHtml(valueHeader)}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
}

function wrapLtrCellContent(text, cellClass = '') {
  const safe = escapeReportHtml(text);
  if (cellClass.includes('datetime') || cellClass.includes('amount')) {
    return `<span dir="ltr" class="report-ltr">${safe}</span>`;
  }
  return safe;
}

function buildTableHtml(report) {
  if (!report.columns.length) return '';

  const colWidth = `${Math.max(8, Math.floor(100 / report.columns.length))}%`;
  const headCells = report.columns
    .map((col) => `<th style="width:${colWidth}">${escapeReportHtml(col.label)}</th>`)
    .join('');

  if (!report.rows.length) {
    const emptyMsg = report.isRTL ? 'لا توجد بيانات' : 'Aucune donnée';
    return `<div class="report-empty">${escapeReportHtml(emptyMsg)}</div>`;
  }

  const bodyRows = report.rows
    .map(
      (row, rowIndex) =>
        `<tr>${row
          .map((cell, colIndex) => {
            const col = report.columns[colIndex];
            const cls = col?.cellClass ? ` class="${col.cellClass}"` : '';
            return `<td${cls}>${wrapLtrCellContent(cell, col?.cellClass || '')}</td>`;
          })
          .join('')}</tr>`
    )
    .join('');

  return `
    <table class="report-data-table">
      <thead><tr>${headCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>`;
}

/**
 * Build complete report HTML from raw page payload.
 * @param {Object} rawData
 * @param {{ lang?: 'fr'|'ar' }} [options]
 */
export function buildReportDocumentHTML(rawData, options = {}) {
  const lang = options.lang === 'ar' ? 'ar' : 'fr';
  const report = normalizeReportPayload(rawData, lang);
  const letterhead = buildReportLetterheadHTML(
    {
      ...rawData,
      titleFr: report.titleFr,
      titleAr: report.titleAr,
      businessInfo: report.businessInfo,
      generatedAt: report.generatedAt
    },
    { lang }
  );

  const fontCss = report.isRTL ? REPORT_ARABIC_HTML_FONT_CSS : '';
  const emptyTitle = escapeReportHtml(report.title);

  return `<!DOCTYPE html>
<html lang="${lang}" dir="${report.isRTL ? 'rtl' : 'ltr'}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${emptyTitle}</title>
  <style>
    ${fontCss}
    ${getReportDocumentCss()}
  </style>
</head>
<body dir="${report.isRTL ? 'rtl' : 'ltr'}">
  <div class="report-document">
    ${letterhead}
    ${buildSummaryHtml(report)}
    ${buildTableHtml(report)}
    <div class="report-footer">Aman ERP</div>
  </div>
</body>
</html>`;
}
